// Data layer for the owner custom-field admin. Talks to /me/fields (CDP L4).
// One thin async fn per endpoint.
//
// TWO screens share it since CP3 L: Configuración → Personas owns the CONTACT
// half, and Procesos → Datos del proceso owns the LEAD half. The endpoint never
// split — `entity` does the discriminating — so this file stays one module.

import { apiClient } from '../../api/client.js'

export type FieldEntityType = 'contact' | 'lead'
export type FieldDataType = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'phone' | 'email'
/** De dónde salen las opciones de una lista (D21-1). El API lo valida con la
 *  misma constante (`@vysto/shared/bot-config/field-kind-map`); aquí se escribe
 *  como tipo para no arrastrar el paquete compartido a una pantalla. */
export type FieldOptionsSource = 'lista' | 'agenda' | 'catalogo'

export interface FieldDefinition {
  id: string
  entity_type: FieldEntityType
  key: string
  label: string
  data_type: FieldDataType
  /** Ya RESUELTAS por el API: si el dato tiene fuente, esta es la lista viva de
   *  hoy, no una copia guardada (una fila con fuente no guarda ninguna). */
  options: string[] | null
  /** De dónde salen. 'lista' = las escribe el cliente. */
  options_source: FieldOptionsSource
  managed: boolean
  active: boolean
  sort: number
}

export interface CreateFieldInput {
  entity_type: FieldEntityType
  key: string
  label: string
  data_type: FieldDataType
  options?: string[]
  options_source?: FieldOptionsSource
  sort?: number
}

export interface UpdateFieldPatch {
  label?: string
  options?: string[] | null
  options_source?: FieldOptionsSource
  active?: boolean
  sort?: number
}

/** Las opciones de una lista, limpias al LEERLAS.
 *
 *  Escribirlas ya las recorta (una por línea, y el trim vive en el zod del API
 *  desde #799), pero las listas guardadas ANTES de ese arreglo siguen cargando
 *  su "
" en la base: al abrir "Editar dato" el salto de línea reaparecía en
 *  el textarea y la lista se veía con renglones en blanco. La migración de
 *  datos limpia lo guardado; esto limpia lo que ya viajó, para que ninguna
 *  pantalla dependa de que la migración haya alcanzado a esa fila. */
export function normalizeFieldOptions(options: string[] | null): string[] | null {
  if (options === null) return null
  // CP-0: la basura vieja no siempre es espacio REAL — hay opciones que cargan
  // los CARACTERES "\n" escritos como texto (backslash + n), y a esos ningún
  // trim los toca. Se quitan solo al FINAL: un "\n" en medio sería contenido.
  return options.map((o) => o.replace(/(?:\\n|\\r|\s)+$/g, '').trim()).filter((o) => o !== '')
}

/** GET /me/fields?entity= — the tenant's custom-field definitions for one entity type. */
export async function listFields(entity: FieldEntityType): Promise<FieldDefinition[]> {
  const res = await apiClient.get<{ data: FieldDefinition[] }>(
    `/me/fields?entity=${encodeURIComponent(entity)}`,
  )
  return res.data.data.map((f) => ({ ...f, options: normalizeFieldOptions(f.options) }))
}

/** POST /me/fields — defines a custom field; `key` is permanent once created. */
export async function createField(input: CreateFieldInput): Promise<FieldDefinition> {
  const res = await apiClient.post<{ data: FieldDefinition }>('/me/fields', input)
  return res.data.data
}

/** Una etapa a la que ocultar un dato le quitó la exigencia, con el proceso al
 *  que pertenece: dos etapas pueden llamarse igual en procesos distintos. */
export interface StrippedStage {
  key: string
  name: string
  pipelineName: string
}

/** PUT /me/fields/:id — edits label/options/active/sort (key and type stay fixed).
 *
 *  Devuelve las etapas que el cambio tocó: ocultar un dato de prospecto lo saca
 *  de los requisitos de toda la operación (si no, las tarjetas quedaban
 *  atoradas pidiendo un dato que ya no se puede capturar), y eso se dice. */
export async function updateField(
  id: string,
  patch: UpdateFieldPatch,
): Promise<{ strippedStages: StrippedStage[] }> {
  const res = await apiClient.put<{ data: { id: string; strippedStages?: StrippedStage[] } }>(
    `/me/fields/${encodeURIComponent(id)}`,
    patch,
  )
  return { strippedStages: res.data.data.strippedStages ?? [] }
}

/** DELETE /me/fields/:id — removes the definition (managed fields are refused
 *  server-side). Igual que ocultar: borrar un dato de prospecto lo saca de los
 *  requisitos de toda la operación, y las etapas tocadas se devuelven para
 *  decirlo (P0-4). */
export async function deleteField(id: string): Promise<{ strippedStages: StrippedStage[] }> {
  const res = await apiClient.delete<{ data: { strippedStages?: StrippedStage[] } }>(
    `/me/fields/${encodeURIComponent(id)}`,
  )
  return { strippedStages: res.data.data.strippedStages ?? [] }
}

/** Human label for a data type (es-MX). */
export const DATA_TYPE_LABELS: Record<FieldDataType, string> = {
  text: 'Texto',
  number: 'Número',
  date: 'Fecha',
  boolean: 'Sí / No',
  enum: 'Lista de opciones',
  phone: 'Teléfono',
  email: 'Correo',
}

/** De dónde salen las opciones, como se ofrece en los dos diálogos (D21-1). La
 *  frase dice la fuente Y lo que hay dentro de ella, porque "De tu Agenda" solo
 *  se entiende si se dice qué toma de ahí. */
export const OPTIONS_SOURCE_LABELS: Record<FieldOptionsSource, string> = {
  lista: 'De una lista (tú la escribes)',
  agenda: 'De tu Agenda (las especialidades de tus médicos)',
  catalogo: 'De tu Catálogo (tus artículos activos)',
}

/** La etiqueta del selector, escrita una vez para las dos pantallas. */
export const OPTIONS_SOURCE_FIELD_LABEL = 'De dónde salen las opciones'

/** Qué es la lista que se está viendo, y que se mantiene sola. */
export const OPTIONS_SOURCE_SENTENCE: Record<Exclude<FieldOptionsSource, 'lista'>, string> = {
  agenda:
    'Estas opciones son las especialidades de tus médicos activos. Se actualizan solas cuando editas tu Agenda.',
  catalogo:
    'Estas opciones son los nombres de tus artículos activos. Se actualizan solas cuando editas tu Catálogo.',
}

/** Una fuente vacía no es una lista en blanco: es un dato que hoy se pregunta
 *  como texto libre, y eso se dice antes de que nadie lo descubra leyendo una
 *  conversación. */
export const OPTIONS_SOURCE_EMPTY: Record<Exclude<FieldOptionsSource, 'lista'>, string> = {
  agenda:
    'Hoy tu Agenda no tiene médicos activos, así que este dato no tiene opciones y el Agente lo preguntará como texto libre.',
  catalogo:
    'Hoy tu Catálogo no tiene artículos activos, así que este dato no tiene opciones y el Agente lo preguntará como texto libre.',
}

/** La puerta a la pantalla donde esa lista SÍ se cambia: nombrar la fuente sin
 *  llevar a ella deja el cambio a media frase. */
export const OPTIONS_SOURCE_LINK: Record<
  Exclude<FieldOptionsSource, 'lista'>,
  { to: string; label: string }
> = {
  agenda: { to: '/agenda', label: 'Ir a Agenda' },
  catalogo: { to: '/catalogo', label: 'Ir a Catálogo' },
}

/** Suggest a stable snake_case key from a human label (accent-folded). */
export function slugifyKey(label: string): string {
  const base = label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
  // Keys must start with a letter.
  return /^[a-z]/.test(base) ? base : `campo_${base}`.slice(0, 64)
}
